/**
 * 机甲键盘常规灯效能力矩阵（与产品表一致）及下发 `setKeyboardLight` 的 LightValues 组装。
 * 列顺序：混色 | 单色 | 亮度 | 速度
 */

export interface LightValues {
  /** 灯效模式 ID（与 SDK mode 一致） */
  mode: number;
  /** 灯效速度 0–4 */
  speed: number;
  /** 灯效亮度 0–10（SDK 刻度） */
  bright: number;
  /** 1 单色 2 混色 */
  type: number;
  /** 单色且需要下发颜色时 */
  color?: { r: number; g: number; b: number };
}

export interface MechaRoutineLightCapabilities {
  /** 混色：是否支持混色模式（可与单色切换时配合 Switch） */
  mixColor: boolean;
  /** 单色：是否支持单色及调色板选色 */
  monoColor: boolean;
  brightness: boolean;
  speed: boolean;
}

const ALL: MechaRoutineLightCapabilities = {
  mixColor: true,
  monoColor: true,
  brightness: true,
  speed: true,
};

/**
 * 主键区：modeId → 能力（与 RgbEffect.value 一致；0=关；19/277=自定义）
 * 数据来源：产品表「混色 / 单色 / 亮度 / 速度」
 */
const MECHA_MAIN_CAPABILITIES: Record<number, MechaRoutineLightCapabilities> = {
  0: {
    mixColor: false,
    monoColor: false,
    brightness: false,
    speed: false,
  },
  // 常亮
  1: {
    mixColor: true,
    monoColor: true,
    brightness: true,
    speed: false,
  },
  2: ALL,
  3: { mixColor: true, monoColor: false, brightness: true, speed: true },
  4: ALL,
  5: ALL,
  6: { mixColor: true, monoColor: false, brightness: true, speed: true },
  7: ALL,
  8: ALL,
  9: ALL,
  10: ALL,
  11: ALL,
  12: ALL,
  13: ALL,
  14: ALL,
  15: { mixColor: true, monoColor: false, brightness: true, speed: true },
  16: { mixColor: true, monoColor: false, brightness: true, speed: true },
  17: { mixColor: true, monoColor: false, brightness: true, speed: true },
  18: ALL,
  19: {
    mixColor: false,
    monoColor: true,
    brightness: false,
    speed: false,
  },
  277: {
    mixColor: false,
    monoColor: true,
    brightness: false,
    speed: false,
  },
};

const DEFAULT_CAP: MechaRoutineLightCapabilities = ALL;

/** UI 亮度滑块 0–20 → SDK 0–10 */
export function uiBrightToMechaSdk(brightnessUi: number): number {
  const n =
    typeof brightnessUi === "number" && !Number.isNaN(brightnessUi)
      ? brightnessUi
      : 8;
  return Math.min(10, Math.max(0, Math.round(n / 2)));
}

export function getMechaRoutineLightCapabilities(
  modeId: number,
  opts?: { isSideStrip?: boolean }
): MechaRoutineLightCapabilities {
  if (opts?.isSideStrip) {
    return ALL;
  }
  const c = MECHA_MAIN_CAPABILITIES[modeId];
  return c ?? DEFAULT_CAP;
}

export interface MechaLightApiParams {
  id: number;
  brightness?: number;
  speed?: number;
  /** 7 表示混色（与现有 hfdParams.colorIndex 对齐） */
  colorIndex?: number;
  color?: { r: number; g: number; b: number };
}

/**
 * 根据能力与当前 UI 状态生成下发 SDK 的 LightValues。
 * 不支持的项使用中性默认值（bright=4, speed=2）。
 */
export function buildMechaLightPayloadFromApiParams(
  params: MechaLightApiParams,
  caps: MechaRoutineLightCapabilities
): LightValues {
  const mode = params.id;
  const speedUi =
    typeof params.speed === "number" && !Number.isNaN(params.speed)
      ? params.speed
      : 4;
  const brightUi =
    typeof params.brightness === "number" && !Number.isNaN(params.brightness)
      ? params.brightness
      : 8;

  const speed = caps.speed ? Math.min(4, Math.max(0, speedUi)) : 2;
  const bright = caps.brightness ? uiBrightToMechaSdk(brightUi) : 4;

  /**
   * 自定义常规灯效：机甲 M71/M80 主灯均走 916 协议，模式号为 277；历史固件曾用 19 仍兼容下发。
   * Mkeyboard 官方 demo 默认 lightType=2（混色），setKeyboardLight 不带 color；
   * 能力表里 mixColor=false 仅表示「界面无混色/单色切换」，若按 mixColor=false 强行发 type=1+单色，
   * 固件往往不收、表现为切到自定义失败。
   */
  if (mode === 277 || mode === 19) {
    /** 自定义模式能力表不写亮度/速度，但仍应携带用户当前滑块值写入固件，避免读回全 0；type 固定 2 */
    const speedOut = Math.min(4, Math.max(0, speedUi));
    const brightOut = uiBrightToMechaSdk(brightUi);
    return { mode, speed: speedOut, bright: brightOut, type: 2 };
  }

  let type: number;
  if (!caps.monoColor) {
    type = 2;
  } else if (!caps.mixColor) {
    type = 1;
  } else {
    type = params.colorIndex === 7 ? 2 : 1;
  }

  const out: LightValues = { mode, speed, bright, type };

  if (
    type === 1 &&
    caps.monoColor &&
    params.color &&
    typeof params.color.r === "number" &&
    typeof params.color.g === "number" &&
    typeof params.color.b === "number"
  ) {
    out.color = {
      r: params.color.r,
      g: params.color.g,
      b: params.color.b,
    };
  }
  return out;
}

/** 主键区非自定义灯效时的亮度/速度/混色记忆，用于切到自定义或刷新后读回补全（固件在自定义下常上报 bright/speed 为 0） */
const MECHA_MAIN_ROUTINE_LIGHT_UI_SNAPSHOT_KEY =
  "eweadn_beiying_mecha_main_routine_light_ui_v1";

export interface MechaMainRoutineLightUiSnapshot {
  brightness: number;
  speed: number;
  colorIndex: number;
}

export function readMechaMainRoutineLightUiSnapshot(): MechaMainRoutineLightUiSnapshot | null {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const raw = ls.getItem(MECHA_MAIN_ROUTINE_LIGHT_UI_SNAPSHOT_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as Partial<MechaMainRoutineLightUiSnapshot>;
    const brightness = Number(o.brightness);
    const speed = Number(o.speed);
    const colorIndex = Number(o.colorIndex);
    if (
      !Number.isFinite(brightness) ||
      !Number.isFinite(speed) ||
      !Number.isFinite(colorIndex)
    ) {
      return null;
    }
    return {
      brightness: Math.min(20, Math.max(0, brightness)),
      speed: Math.min(4, Math.max(0, speed)),
      colorIndex: colorIndex === 7 ? 7 : 0,
    };
  } catch {
    return null;
  }
}

export function writeMechaMainRoutineLightUiSnapshot(
  s: MechaMainRoutineLightUiSnapshot
): void {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return;
    ls.setItem(
      MECHA_MAIN_ROUTINE_LIGHT_UI_SNAPSHOT_KEY,
      JSON.stringify({
        brightness: Math.min(20, Math.max(0, Number(s.brightness) || 0)),
        speed: Math.min(4, Math.max(0, Number(s.speed) || 0)),
        colorIndex: Number(s.colorIndex) === 7 ? 7 : 0,
      })
    );
  } catch {
    /* ignore */
  }
}
